/**
 * FeedSearchPanelContent — canonical feed-style search panel (packages/ui)
 *
 * Part of the discovery domain (feed surface type).
 * Used by Kloutforce Home and Katalog feed surfaces via thin wrappers.
 *
 * Generic over a Section key so each surface can supply its own enum
 * (e.g. 'news' | 'pinned' | 'following' for Home, 'active' | 'paused' | 'ended' for Katalag).
 */
import * as React from 'react';
import { Search, X, Clock } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface FeedSectionShortcut<TSection extends string> {
  key: TSection;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

export interface FeedSearchPanelContentProps<TSection extends string = string> {
  /** Current draft search query */
  searchQuery: string;
  /** Update draft query */
  onSearchQueryChange: (q: string) => void;
  /** Commit search (optionally with explicit query) */
  onExecuteSearch: (explicitQuery?: string) => void;
  /** Currently active sections (committed) */
  activeSections: TSection[];
  /** Toggle a section */
  onToggleSection: (section: TSection) => void;
  /** Quick-filter shortcut definitions */
  sectionShortcuts: FeedSectionShortcut<TSection>[];
  /** localStorage namespace for recent searches (e.g. "kloutforce-home") */
  recentSearchesKey: string;
  /** Input placeholder */
  placeholder?: string;
}

export function FeedSearchPanelContent<TSection extends string>({
  searchQuery,
  onSearchQueryChange,
  onExecuteSearch,
  activeSections,
  onToggleSection,
  sectionShortcuts,
  recentSearchesKey,
  placeholder = 'Search...',
}: FeedSearchPanelContentProps<TSection>) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const storageKey = `${recentSearchesKey}-recent-searches`;

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const [recentSearches, setRecentSearches] = React.useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch {
      return [];
    }
  });

  const addRecent = (q: string) => {
    if (!q.trim()) return;
    const updated = [q.trim(), ...recentSearches.filter((r) => r !== q.trim())].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
  };

  const clearRecents = () => {
    setRecentSearches([]);
    localStorage.removeItem(storageKey);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) addRecent(q);
    onExecuteSearch(q);
  };

  const handleRecentClick = (q: string) => {
    onSearchQueryChange(q);
    onExecuteSearch(q);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Search input */}
      <div className="px-4 py-3">
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              placeholder={placeholder}
              className="w-full pl-9 pr-9 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => onSearchQueryChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 space-y-5">
        {/* Quick section shortcuts */}
        {sectionShortcuts.length > 0 && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
              Quick Filters
            </label>
            <div className="flex flex-wrap gap-2">
              {sectionShortcuts.map((s) => {
                const isActive = activeSections.includes(s.key);
                const Icon = s.icon;
                return (
                  <button
                    key={s.key}
                    onClick={() => onToggleSection(s.key)}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card border border-border text-foreground hover:bg-muted',
                    )}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent searches */}
        {!searchQuery && recentSearches.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Recent Searches
              </label>
              <button
                onClick={clearRecents}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            </div>
            <div className="space-y-1">
              {recentSearches.map((q, i) => (
                <button
                  key={`${q}-${i}`}
                  onClick={() => handleRecentClick(q)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-muted transition-colors text-left"
                >
                  <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer with Search button */}
      <div
        className="shrink-0 sticky bottom-0 bg-transparent border-t border-border px-4 py-3"
        data-drawer-footer
      >
        <button
          data-panel-search="true"
          type="button"
          onClick={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
          className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          Search
        </button>
      </div>
    </div>
  );
}

export default FeedSearchPanelContent;